import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem,
  IconButton,
  TablePagination,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { finishingApi, repairsApi } from '../api/production';
import { useAuth } from '../contexts/AuthContext';
import Avatar from '@mui/material/Avatar';
import { GARMENT_SIZES } from '../constants/garmentConstants';
import DesignPreviewDialog from '../components/DesignPreviewDialog';
import designApi from '../api/design';
import { getDesigns } from '../services/api';
import api from '../services/api';

interface PendingQcPart {
  design_no: string;
  garment_part: string;
  qc_count: number;
  qc_from_batches: number;
  opening_entry_qty: number;
  press_received_qty: number;
  damaged_pieces: number;
  barcoded_pieces: number;
  in_repair_pieces: number;
  in_press: boolean;
  in_press_qty: number;
  external_job_works: Array<{ job_type: string; vendor_name: string }>;
  image_url?: string | null;
}

interface GroupedDesign {
  design_no: string;
  image_url: string | null;
  parts: PendingQcPart[];
  total_qc_count: number;
  total_opening_entry: number;
  total_press_received: number;
  total_barcoded: number;
  total_damaged: number;
  total_in_repair: number;
  in_press: boolean;
  in_press_qty: number;
  external_job_works: Array<{ job_type: string; vendor_name: string }>;
}

interface PartForm {
  good_qty: number;
  damage_qty: number;
  permanent_damage_qty: number;
}

export default function QcStationPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: authUser } = useAuth();
  const isMaster = authUser?.role === 'MASTER';
  
  const [groupedDesigns, setGroupedDesigns] = useState<GroupedDesign[]>([]);
  const [filteredDesigns, setFilteredDesigns] = useState<GroupedDesign[]>([]);
  const [selectedDesign, setSelectedDesign] = useState<GroupedDesign | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [repairTypes, setRepairTypes] = useState<any[]>([]);
  const [designSearchFilter, setDesignSearchFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [inPressFilter, setInPressFilter] = useState<boolean | null>(null);
  const [externalJobFilter, setExternalJobFilter] = useState<boolean | null>(null);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'opening' | 'press'>('all');
  const [garmentPartFilter, setGarmentPartFilter] = useState<string[]>([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Design Preview Dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedPreviewDesign, setSelectedPreviewDesign] = useState<any>(null);
  const [selectedPreviewTopDesign, setSelectedPreviewTopDesign] = useState<any>(null);
  const [selectedPreviewBottomDesign, setSelectedPreviewBottomDesign] = useState<any>(null);
  const [loadingCuttingData, setLoadingCuttingData] = useState(false);
  const [selectedDesignNo, setSelectedDesignNo] = useState<string | null>(null);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);

  // Processing state — per garment part
  const [partForms, setPartForms] = useState<Map<string, PartForm>>(new Map());
  const [manuallyEdited, setManuallyEdited] = useState<Set<string>>(new Set());
  const [processNotes, setProcessNotes] = useState('');
  const [includeSample, setIncludeSample] = useState(false);

  const [repairs, setRepairs] = useState<{
    garment_part: string;
    size: string;
    qty: number;
    damage_type_id: number;
    notes: string;
  }[]>([]);

  useEffect(() => {
    fetchPendingDesigns();
    fetchRepairTypes();
  }, []);

  useEffect(() => {
    let filtered = groupedDesigns;

    if (designSearchFilter) {
      filtered = filtered.filter(d =>
        d.design_no.toLowerCase().includes(designSearchFilter.toLowerCase())
      );
    }

    if (inPressFilter !== null) {
      filtered = filtered.filter(d => inPressFilter ? d.in_press : !d.in_press);
    }

    if (externalJobFilter !== null) {
      filtered = filtered.filter(d =>
        externalJobFilter ? d.external_job_works.length > 0 : d.external_job_works.length === 0
      );
    }

    if (sourceFilter === 'opening') {
      filtered = filtered.filter(d => d.total_opening_entry > 0);
    } else if (sourceFilter === 'press') {
      filtered = filtered.filter(d => d.total_press_received > 0);
    }

    if (garmentPartFilter.length > 0) {
      filtered = filtered.filter(d =>
        d.parts.some(p => garmentPartFilter.includes(p.garment_part))
      );
    }

    const sorted = [...filtered].sort((a, b) =>
      sortOrder === 'asc' ? a.total_qc_count - b.total_qc_count : b.total_qc_count - a.total_qc_count
    );

    setFilteredDesigns(sorted);
    setPage(0);
  }, [groupedDesigns, designSearchFilter, sortOrder, inPressFilter, externalJobFilter, sourceFilter, garmentPartFilter]);

  // All unique garment parts across all designs
  const availableGarmentParts = useMemo(() => {
    const parts = new Set<string>();
    groupedDesigns.forEach(d => d.parts.forEach(p => parts.add(p.garment_part)));
    return Array.from(parts).sort();
  }, [groupedDesigns]);

  // Paginated designs
  const paginatedDesigns = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredDesigns.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredDesigns, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchPendingDesigns = async () => {
    try {
      setLoading(true);
      const data: PendingQcPart[] = await finishingApi.getPendingQcDesigns();

      // Group by design_no
      const grouped = new Map<string, GroupedDesign>();
      data.forEach(part => {
        const existing = grouped.get(part.design_no);
        if (existing) {
          existing.parts.push(part);
          existing.total_qc_count += part.qc_count;
          existing.total_opening_entry += (part.opening_entry_qty || 0);
          existing.total_press_received += (part.press_received_qty || 0);
          existing.total_barcoded += part.barcoded_pieces;
          existing.total_damaged += part.damaged_pieces;
          existing.total_in_repair += part.in_repair_pieces;
          if (part.in_press) existing.in_press = true;
          existing.in_press_qty += part.in_press_qty;
          if (!existing.image_url && part.image_url) existing.image_url = part.image_url;
        } else {
          grouped.set(part.design_no, {
            design_no: part.design_no,
            image_url: part.image_url || null,
            parts: [part],
            total_qc_count: part.qc_count,
            total_opening_entry: part.opening_entry_qty || 0,
            total_press_received: part.press_received_qty || 0,
            total_barcoded: part.barcoded_pieces,
            total_damaged: part.damaged_pieces,
            total_in_repair: part.in_repair_pieces,
            in_press: part.in_press,
            in_press_qty: part.in_press_qty,
            external_job_works: part.external_job_works || [],
          });
        }
      });

      setGroupedDesigns(Array.from(grouped.values()));
    } catch (err: any) {
      console.error('Error fetching pending designs:', err);
      setError('Failed to load pending QC designs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairTypes = async () => {
    try {
      const data = await repairsApi.getTypes();
      setRepairTypes(data);
    } catch (err) {
      console.error('Error fetching repair types:', err);
    }
  };

  const handleOpenDesignPreview = async (designNo: string, previewImage?: string | null) => {
    setSelectedDesignNo(designNo);
    setSelectedPreviewImage(previewImage || null);
    setLoadingCuttingData(true);
    setPreviewDialogOpen(true);
    setSelectedPreviewDesign(null);
    setSelectedPreviewTopDesign(null);
    setSelectedPreviewBottomDesign(null);

    try {
      // Fetch design by design_no
      const designsResponse = await getDesigns();
      const designs = Array.isArray(designsResponse?.data) 
        ? designsResponse.data 
        : Array.isArray(designsResponse) 
          ? designsResponse 
          : [];
      const design = designs.find((d: any) => d.design_no === designNo);
      
      if (design) {
        // Fetch full design details
        try {
          const fullDesign = await designApi.getById(design.id);
          // Handle both response.data and direct response
          if (fullDesign?.data) {
            setSelectedPreviewDesign(fullDesign.data);
          } else if (fullDesign) {
            setSelectedPreviewDesign(fullDesign);
          } else {
            setSelectedPreviewDesign(design);
          }
        } catch (e: any) {
          console.error('Error fetching full design details:', e);
          // Use the basic design object if full fetch fails
          setSelectedPreviewDesign(design);
        }
      } else {
        console.warn(`Design ${designNo} not found in designs list`);
      }

      // Fetch design cutting data
      const cuttingResponse = await api.get('/design-cutting');
      if (cuttingResponse.data && Array.isArray(cuttingResponse.data)) {
        const allCuttingData = cuttingResponse.data;
        const topDesign = allCuttingData.find((d: any) => 
          d.design_no === designNo && d.garment_part === 'TOP'
        );
        const bottomDesign = allCuttingData.find((d: any) => 
          d.design_no === designNo && d.garment_part === 'BOTTOM'
        );
        setSelectedPreviewTopDesign(topDesign);
        setSelectedPreviewBottomDesign(bottomDesign);
      }
    } catch (error) {
      console.error('Error fetching design preview data:', error);
    } finally {
      setLoadingCuttingData(false);
    }
  };

  const handleCloseDesignPreview = () => {
    setPreviewDialogOpen(false);
    setSelectedPreviewDesign(null);
    setSelectedPreviewTopDesign(null);
    setSelectedPreviewBottomDesign(null);
    setSelectedDesignNo(null);
    setSelectedPreviewImage(null);
    setLoadingCuttingData(false);
  };

  const handleDesignSelect = (design: GroupedDesign) => {
    setSelectedDesign(design);
    // Initialize per-part forms with zeros
    const forms = new Map<string, PartForm>();
    design.parts.forEach(p => {
      forms.set(p.garment_part, { good_qty: 0, damage_qty: 0, permanent_damage_qty: 0 });
    });
    setPartForms(forms);
    setManuallyEdited(new Set());
    setProcessNotes('');
    setIncludeSample(false);
    setRepairs([]);
    setDialogOpen(true);
  };

  const handleClearView = () => {
    setSelectedDesign(null);
    setDialogOpen(false);
    setPartForms(new Map());
    setManuallyEdited(new Set());
    setProcessNotes('');
    setIncludeSample(false);
    setRepairs([]);
  };

  // When a part form changes, auto-fill non-manually-edited parts from the first part
  const handlePartFormChange = (garmentPart: string, field: keyof PartForm, value: number) => {
    setPartForms(prev => {
      const next = new Map(prev);
      const current = next.get(garmentPart) || { good_qty: 0, damage_qty: 0, permanent_damage_qty: 0 };
      next.set(garmentPart, { ...current, [field]: Math.max(0, value) });

      // If this is the first part, auto-fill other non-manually-edited parts
      const firstPart = selectedDesign?.parts[0]?.garment_part;
      if (garmentPart === firstPart) {
        const firstForm = next.get(firstPart)!;
        selectedDesign?.parts.forEach(p => {
          if (p.garment_part !== firstPart && !manuallyEdited.has(p.garment_part)) {
            // Auto-fill but cap at available qty for that part
            next.set(p.garment_part, {
              good_qty: Math.min(firstForm.good_qty, p.qc_count),
              damage_qty: Math.min(firstForm.damage_qty, Math.max(0, p.qc_count - Math.min(firstForm.good_qty, p.qc_count))),
              permanent_damage_qty: Math.min(
                firstForm.permanent_damage_qty,
                Math.max(0, p.qc_count - Math.min(firstForm.good_qty, p.qc_count) - Math.min(firstForm.damage_qty, Math.max(0, p.qc_count - Math.min(firstForm.good_qty, p.qc_count))))
              ),
            });
          }
        });
      } else {
        // Mark this part as manually edited
        setManuallyEdited(prevEdited => new Set(prevEdited).add(garmentPart));
      }

      return next;
    });
  };

  const addRepair = () => {
    setRepairs(prev => [...prev, {
      garment_part: selectedDesign?.parts[0]?.garment_part || 'TOP',
      size: GARMENT_SIZES[0] || 'M',
      qty: 1,
      damage_type_id: repairTypes[0]?.id || 0,
      notes: ''
    }]);
  };

  const removeRepair = (index: number) => {
    setRepairs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      if (!selectedDesign) return;

      // Build parts array from partForms
      const partsToSubmit: Array<{ garment_part: string; good_qty: number; damage_qty: number; permanent_damage_qty: number }> = [];
      let totalGood = 0, totalDamage = 0, totalPermanent = 0;

      selectedDesign.parts.forEach(p => {
        const form = partForms.get(p.garment_part);
        if (!form) return;
        const good = form.good_qty || 0;
        const damage = form.damage_qty || 0;
        const permanent = form.permanent_damage_qty || 0;
        const partTotal = good + damage + permanent;
        if (partTotal > 0) {
          // Validate per part
          if (partTotal > p.qc_count + (includeSample && p.garment_part === selectedDesign.parts[0]?.garment_part ? 1 : 0)) {
            throw new Error(`${p.garment_part}: Total (${partTotal}) exceeds available (${p.qc_count})`);
          }
          partsToSubmit.push({ garment_part: p.garment_part, good_qty: good, damage_qty: damage, permanent_damage_qty: permanent });
          totalGood += good;
          totalDamage += damage;
          totalPermanent += permanent;
        }
      });

      if (partsToSubmit.length === 0) {
        setError('Enter quantities for at least one garment part');
        return;
      }

      // Validate repair details
      const totalRepairQty = repairs.reduce((sum, r) => sum + r.qty, 0);
      if (totalDamage > 0 && totalRepairQty === 0) {
        setError('Please add repair details for damaged pieces using the "Add Repair" button below.');
        return;
      }
      if (totalRepairQty > 0 && totalRepairQty !== totalDamage) {
        setError(`Repair quantity (${totalRepairQty}) must match total damage quantity (${totalDamage})`);
        return;
      }

      const processData = {
        design_no: selectedDesign.design_no,
        parts: partsToSubmit,
        repairs: repairs.filter(r => r.qty > 0),
        notes: processNotes,
        include_sample: includeSample
      };

      setLoading(true);
      await finishingApi.processQc(processData);

      setSuccess(`QC processed successfully! ${totalGood} pieces barcoded across ${partsToSubmit.length} part(s).`);
      setDialogOpen(false);
      setSelectedDesign(null);

      setTimeout(() => { fetchPendingDesigns(); }, 1000);
    } catch (err: any) {
      console.error('Error processing QC:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process QC');
    } finally {
      setLoading(false);
    }
  };

  const [rectifying, setRectifying] = useState(false);

  const handleRectifyQc = async () => {
    if (!window.confirm(
      'This will mirror TOP barcoded/damaged quantities to BOTTOM/JACKET parts that are pending but were never processed separately.\n\nThis is a one-time fix for the per-part tracking migration. Continue?'
    )) return;

    setRectifying(true);
    try {
      const result = await finishingApi.rectifyQc();
      setSuccess(`${result.message}. Barcoded: ${result.total_barcoded}, Damaged: ${result.total_damaged}`);
      fetchPendingDesigns();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to rectify QC data');
    } finally {
      setRectifying(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              QC Station
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Process stitched pieces at QC stage. Partial processing is allowed.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isMaster && (
              <Button
                variant="outlined"
                color="warning"
                onClick={handleRectifyQc}
                disabled={rectifying}
                size="small"
              >
                {rectifying ? 'Rectifying...' : 'Rectify QC'}
              </Button>
            )}
            {selectedDesign && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearView}
              >
                Clear View
              </Button>
            )}
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Pending Designs
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {groupedDesigns.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total QC Pending
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {groupedDesigns.reduce((sum, d) => sum + d.total_qc_count, 0)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {(() => {
                    const partTotals = new Map<string, number>();
                    groupedDesigns.forEach(d => d.parts.forEach(p => {
                      partTotals.set(p.garment_part, (partTotals.get(p.garment_part) || 0) + p.qc_count);
                    }));
                    return Array.from(partTotals.entries()).map(([part, count]) => (
                      <Chip key={part} label={`${part}: ${count}`} size="small" variant="outlined" />
                    ));
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  From Opening Entry
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {groupedDesigns.reduce((sum, d) => sum + d.total_opening_entry, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  From Press
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="secondary.main">
                  {groupedDesigns.reduce((sum, d) => sum + d.total_press_received, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search Filter and Sort */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search by Design"
            variant="outlined"
            size="small"
            value={designSearchFilter}
            onChange={(e) => setDesignSearchFilter(e.target.value)}
            placeholder="Enter design number..."
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>In Press</InputLabel>
            <Select
              value={inPressFilter === null ? '' : inPressFilter ? 'yes' : 'no'}
              label="In Press"
              onChange={(e) => {
                const val = e.target.value;
                setInPressFilter(val === '' ? null : val === 'yes');
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">In Press</MenuItem>
              <MenuItem value="no">Not In Press</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>External Jobs</InputLabel>
            <Select
              value={externalJobFilter === null ? '' : externalJobFilter ? 'yes' : 'no'}
              label="External Jobs"
              onChange={(e) => {
                const val = e.target.value;
                setExternalJobFilter(val === '' ? null : val === 'yes');
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">Has External Jobs</MenuItem>
              <MenuItem value="no">No External Jobs</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Garment Part</InputLabel>
            <Select
              multiple
              value={garmentPartFilter}
              label="Garment Part"
              onChange={(e) => {
                const val = e.target.value;
                setGarmentPartFilter(typeof val === 'string' ? val.split(',') : val);
              }}
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {availableGarmentParts.map(part => (
                <MenuItem key={part} value={part}>
                  <Checkbox checked={garmentPartFilter.includes(part)} size="small" />
                  <ListItemText primary={part} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={sourceFilter}
              label="Source"
              onChange={(e) => setSourceFilter(e.target.value as 'all' | 'opening' | 'press')}
            >
              <MenuItem value="all">All Sources</MenuItem>
              <MenuItem value="opening">Opening Entry</MenuItem>
              <MenuItem value="press">Press Received</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Sort by QC Count</InputLabel>
            <Select
              value={sortOrder}
              label="Sort by QC Count"
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
            >
              <MenuItem value="asc">Low to High</MenuItem>
              <MenuItem value="desc">High to Low</MenuItem>
            </Select>
          </FormControl>
          {(designSearchFilter || inPressFilter !== null || externalJobFilter !== null || sourceFilter !== 'all' || garmentPartFilter.length > 0) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setDesignSearchFilter('');
                setInPressFilter(null);
                setExternalJobFilter(null);
                setSourceFilter('all');
                setGarmentPartFilter([]);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Pending Designs Table */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Pending Designs {
            (designSearchFilter || inPressFilter !== null || externalJobFilter !== null || sourceFilter !== 'all' || garmentPartFilter.length > 0)
              ? `(${filteredDesigns.length} of ${groupedDesigns.length})`
              : `(${groupedDesigns.length})`
          }
        </Typography>
        
        {isMobile ? (
          // Mobile Card View
          <>
            {filteredDesigns.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {designSearchFilter ? 'No designs found' : 'No pending QC work'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {designSearchFilter ? 'Try adjusting your search filter' : 'All designs have been processed!'}
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {paginatedDesigns.map((design, index) => (
                    <Grid item xs={12} sm={6} key={design.design_no}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                              {design.image_url ? (
                                <Box
                                  component="img"
                                  src={design.image_url}
                                  alt={design.design_no}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleOpenDesignPreview(design.design_no, design.image_url);
                                  }}
                                  sx={{
                                    width: 80, height: 160, objectFit: 'cover', borderRadius: 1,
                                    border: '1px solid', borderColor: 'divider', cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 }
                                  }}
                                />
                              ) : (
                                <Avatar
                                  variant="rounded"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenDesignPreview(design.design_no); }}
                                  sx={{ width: 80, height: 160, bgcolor: 'grey.200', fontSize: '1.5rem', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                >
                                  {design.design_no.substring(0, 2)}
                                </Avatar>
                              )}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="caption" color="text.secondary" gutterBottom>
                                #{page * rowsPerPage + index + 1}
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" gutterBottom>
                                {design.design_no}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                {design.in_press && (
                                  <Chip label="In Press" color="info" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                )}
                                {design.external_job_works.map((job, idx) => (
                                  <Chip key={idx} label={`${job.job_type} - ${job.vendor_name}`} color="secondary" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                ))}
                              </Box>
                              {/* Per-part chips */}
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {design.parts.map(p => (
                                  <Chip
                                    key={p.garment_part}
                                    label={`${p.garment_part}: ${p.qc_count}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 22, fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Box>

                          {/* Source breakdown */}
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, mb: 1 }}>
                            {design.total_opening_entry > 0 && (
                              <Chip label={`OE: ${design.total_opening_entry}`} size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {design.total_press_received > 0 && (
                              <Chip label={`Press: ${design.total_press_received}`} size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                          </Box>

                          <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'primary.light' }}>
                                <Typography variant="caption" color="white">QC Pending</Typography>
                                <Typography variant="body2" fontWeight="bold" color="white">{design.total_qc_count}</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'success.light' }}>
                                <Typography variant="caption" color="white">Barcoded</Typography>
                                <Typography variant="body2" fontWeight="bold" color="white">{design.total_barcoded}</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'error.light' }}>
                                <Typography variant="caption" color="white">Damaged</Typography>
                                <Typography variant="body2" fontWeight="bold" color="white">{design.total_damaged}</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6}>
                              <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'warning.light' }}>
                                <Typography variant="caption" color="white">In Repair</Typography>
                                <Typography variant="body2" fontWeight="bold" color="white">{design.total_in_repair}</Typography>
                              </Paper>
                            </Grid>
                          </Grid>

                          <Button variant="contained" fullWidth onClick={() => handleDesignSelect(design)}>
                            Process
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Mobile Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <TablePagination
                    component="div"
                    count={filteredDesigns.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[50, 100, 200, 500]}
                    labelRowsPerPage="Rows:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  />
                </Box>
              </>
            )}
          </>
        ) : (
          // Desktop Table View
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>S.No.</TableCell>
                    <TableCell width={120}>Image</TableCell>
                    <TableCell>Design No</TableCell>
                    <TableCell align="center">Opening Entry</TableCell>
                    <TableCell align="center">Press Received</TableCell>
                    <TableCell align="center">QC Pending</TableCell>
                    <TableCell align="center">Barcoded</TableCell>
                    <TableCell align="center">Damaged</TableCell>
                    <TableCell align="center">In Repair</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDesigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Box sx={{ py: 4 }}>
                          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            {designSearchFilter ? 'No designs found' : 'No pending QC work'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {designSearchFilter ? 'Try adjusting your search filter' : 'All designs have been processed!'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedDesigns.map((design, index) => (
                      <TableRow
                        key={design.design_no}
                        hover
                        onClick={() => handleOpenDesignPreview(design.design_no, design.image_url)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {page * rowsPerPage + index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {design.image_url ? (
                            <Box
                              component="img"
                              src={design.image_url}
                              alt={design.design_no}
                              onClick={(e) => { e.stopPropagation(); handleOpenDesignPreview(design.design_no, design.image_url); }}
                              sx={{ width: 80, height: 160, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                            />
                          ) : (
                            <Avatar
                              variant="rounded"
                              onClick={(e) => { e.stopPropagation(); handleOpenDesignPreview(design.design_no); }}
                              sx={{ width: 80, height: 160, bgcolor: 'grey.200', fontSize: '1.5rem', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                            >
                              {design.design_no.substring(0, 2)}
                            </Avatar>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {design.design_no}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {design.in_press && (
                              <Chip label="In Press" color="info" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {design.external_job_works.map((job, idx) => (
                              <Chip key={idx} label={`${job.job_type} - ${job.vendor_name}`} color="secondary" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            ))}
                          </Box>
                          {/* Per-part chips */}
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {design.parts.map(p => (
                              <Chip
                                key={p.garment_part}
                                label={`${p.garment_part}: ${p.qc_count}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {design.total_opening_entry > 0 ? design.total_opening_entry : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {design.total_press_received > 0 ? design.total_press_received : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={design.total_qc_count} color="primary" size="small" />
                        </TableCell>
                        <TableCell align="center">{design.total_barcoded}</TableCell>
                        <TableCell align="center">{design.total_damaged}</TableCell>
                        <TableCell align="center">{design.total_in_repair}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleDesignSelect(design); }}
                          >
                            Process
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Desktop Pagination */}
            {filteredDesigns.length > 0 && (
              <TablePagination
                component="div"
                count={filteredDesigns.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[50, 100, 200, 500]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
              />
            )}
          </>
        )}
      </Paper>

      {/* Processing Dialog — Per Garment Part */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Process QC: {selectedDesign?.design_no}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }} icon={false}>
              Enter quantities for each garment part. Editing the first part auto-fills others.
            </Alert>

            {/* Per-part sections */}
            {selectedDesign?.parts.map((part, idx) => {
              const form = partForms.get(part.garment_part) || { good_qty: 0, damage_qty: 0, permanent_damage_qty: 0 };
              const partTotal = form.good_qty + form.damage_qty + form.permanent_damage_qty;
              const isFirst = idx === 0;
              const isEdited = manuallyEdited.has(part.garment_part);

              return (
                <Paper
                  key={part.garment_part}
                  sx={{ p: 2, mb: 2, border: '1px solid', borderColor: isFirst ? 'primary.main' : 'divider' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {part.garment_part}
                      </Typography>
                      <Chip
                        label={`${part.qc_count} available`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    {!isFirst && isEdited && (
                      <Chip label="Edited" size="small" color="warning" sx={{ height: 20 }} />
                    )}
                    {!isFirst && !isEdited && (
                      <Chip label="Auto-filled" size="small" color="default" sx={{ height: 20 }} />
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextField
                        label="Good"
                        type="number"
                        fullWidth
                        size="small"
                        value={form.good_qty || ''}
                        onChange={(e) => handlePartFormChange(part.garment_part, 'good_qty', parseInt(e.target.value) || 0)}
                        InputProps={{ inputProps: { min: 0 } }}
                        InputLabelProps={{ sx: { fontWeight: 'bold' } }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Damage"
                        type="number"
                        fullWidth
                        size="small"
                        value={form.damage_qty || ''}
                        onChange={(e) => handlePartFormChange(part.garment_part, 'damage_qty', parseInt(e.target.value) || 0)}
                        InputProps={{ inputProps: { min: 0 } }}
                        InputLabelProps={{ sx: { fontWeight: 'bold' } }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Permanent"
                        type="number"
                        fullWidth
                        size="small"
                        value={form.permanent_damage_qty || ''}
                        onChange={(e) => handlePartFormChange(part.garment_part, 'permanent_damage_qty', parseInt(e.target.value) || 0)}
                        InputProps={{ inputProps: { min: 0 } }}
                        InputLabelProps={{ sx: { fontWeight: 'bold' } }}
                      />
                    </Grid>
                  </Grid>

                  {partTotal > part.qc_count && (
                    <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                      Total ({partTotal}) exceeds available ({part.qc_count})
                    </Alert>
                  )}

                  {/* Include sample checkbox on first part only */}
                  {isFirst && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeSample}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setIncludeSample(checked);
                            if (checked) {
                              handlePartFormChange(part.garment_part, 'good_qty', form.good_qty + 1);
                            } else {
                              handlePartFormChange(part.garment_part, 'good_qty', Math.max(0, form.good_qty - 1));
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Include Sample (+1 piece)"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Paper>
              );
            })}

            {/* Summary totals */}
            {(() => {
              let totalGood = 0, totalDmg = 0, totalPerm = 0;
              partForms.forEach(f => { totalGood += f.good_qty; totalDmg += f.damage_qty; totalPerm += f.permanent_damage_qty; });
              return (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.light' }}>
                      <Typography variant="h6" color="white">{totalGood}</Typography>
                      <Typography variant="caption" color="white">Good</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'warning.light' }}>
                      <Typography variant="h6" color="white">{totalDmg}</Typography>
                      <Typography variant="caption" color="white">Damage</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'error.light' }}>
                      <Typography variant="h6" color="white">{totalPerm}</Typography>
                      <Typography variant="caption" color="white">Permanent</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              );
            })()}

            {/* Notes */}
            <TextField
              label="Notes (Optional)"
              fullWidth
              multiline
              rows={2}
              value={processNotes}
              onChange={(e) => setProcessNotes(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Repair Details Section */}
            {(() => {
              let totalDmg = 0;
              partForms.forEach(f => { totalDmg += f.damage_qty; });
              if (totalDmg <= 0) return null;
              return (
                <Box sx={{ mt: 1 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {totalDmg} piece(s) marked for repair. Please specify damage details.
                  </Alert>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Repair Details</Typography>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRepair}>
                      Add Repair
                    </Button>
                  </Box>

                  {repairs.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        Click "Add Repair" to specify damage details
                      </Typography>
                    </Paper>
                  ) : (
                    <Grid container spacing={2}>
                      {repairs.map((repair, index) => (
                        <Grid item xs={12} key={index}>
                          <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  select label="Part" size="small" fullWidth
                                  value={repair.garment_part}
                                  onChange={(e) => { const u = [...repairs]; u[index].garment_part = e.target.value; setRepairs(u); }}
                                >
                                  {selectedDesign?.parts.map(p => (
                                    <MenuItem key={p.garment_part} value={p.garment_part}>{p.garment_part}</MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  select label="Size" size="small" fullWidth value={repair.size}
                                  onChange={(e) => { const u = [...repairs]; u[index].size = e.target.value; setRepairs(u); }}
                                >
                                  {GARMENT_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </TextField>
                              </Grid>
                              <Grid item xs={6} sm={1}>
                                <TextField
                                  label="Qty" type="number" size="small" fullWidth value={repair.qty}
                                  onChange={(e) => { const u = [...repairs]; u[index].qty = parseInt(e.target.value) || 0; setRepairs(u); }}
                                  InputProps={{ inputProps: { min: 1 } }}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <TextField
                                  select label="Repair Type" size="small" fullWidth value={repair.damage_type_id}
                                  onChange={(e) => { const u = [...repairs]; u[index].damage_type_id = parseInt(e.target.value); setRepairs(u); }}
                                >
                                  {repairTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                                </TextField>
                              </Grid>
                              <Grid item xs={10} sm={3}>
                                <TextField
                                  label="Notes" size="small" fullWidth value={repair.notes} placeholder="Optional"
                                  onChange={(e) => { const u = [...repairs]; u[index].notes = e.target.value; setRepairs(u); }}
                                />
                              </Grid>
                              <Grid item xs={2} sm={1}>
                                <IconButton color="error" size="small" onClick={() => removeRepair(index)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {repairs.length > 0 && (
                    <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.light' }}>
                      <Typography variant="body2" color="white">
                        <strong>Total Repairs:</strong> {repairs.reduce((sum, r) => sum + r.qty, 0)} pieces
                      </Typography>
                    </Paper>
                  )}
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Processing...' : 'Process QC'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Design Preview Dialog */}
      <DesignPreviewDialog
        open={previewDialogOpen}
        design={selectedPreviewDesign}
        designNo={selectedDesignNo || selectedPreviewDesign?.design_no}
        previewImage={selectedPreviewImage || selectedPreviewDesign?.image_url || null}
        topDesign={selectedPreviewTopDesign}
        bottomDesign={selectedPreviewBottomDesign}
        loadingCuttingData={loadingCuttingData}
        onClose={handleCloseDesignPreview}
      />
    </Box>
  );
}

